import { useEffect, useState } from "react"
import { collection, query, where, onSnapshot, Firestore } from "firebase/firestore"

const STALE_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

export interface ActiveJob {
  job_id: string
  status: JobStatus
  operation: string
  media_kind: "pictures" | "videos"
  prompts: string[]
  created_at: any
  started_at: any
  error_message: string | null
  isStale: boolean
  completed_count: number
  total_count: number
  source_picture_id: string
}

export function useActiveJobs(
  db: Firestore,
  projectId: string | undefined,
  uid: string | undefined,
): ActiveJob[] {
  const [jobs, setJobs] = useState<ActiveJob[]>([])

  useEffect(() => {
    if (!projectId || !uid) {
      setJobs([])
      return
    }

    const jobsRef = collection(db, "projects", projectId, "jobs")
    const q = query(
      jobsRef,
      where("status", "in", ["pending", "running"]),
      where("created_by", "==", uid),
    )

    const unsub = onSnapshot(q, (snap) => {
      const now = Date.now()
      const active: ActiveJob[] = snap.docs.map((doc) => {
        const data = doc.data()
        const createdMs = data.created_at?.toMillis?.() ?? 0
        const outputIds: string[] = data.output_picture_ids || []
        const prompts: string[] = data.prompts || []
        return {
          job_id: doc.id,
          status: data.status,
          operation: data.operation,
          media_kind: data.media_kind,
          prompts,
          created_at: data.created_at,
          started_at: data.started_at,
          error_message: data.error_message,
          isStale: createdMs > 0 && (now - createdMs) > STALE_THRESHOLD_MS,
          completed_count: outputIds.length,
          total_count: prompts.length,
          source_picture_id: data.source_picture_id || "",
        }
      })
      active.sort((a, b) => {
        const ta = a.created_at?.toMillis?.() ?? 0
        const tb = b.created_at?.toMillis?.() ?? 0
        return tb - ta
      })
      setJobs(active)
    })

    return () => unsub()
  }, [db, projectId, uid])

  return jobs
}
